/**
 * @module FCUtils
 * @description Shared utility functions used across the theme: media-query helpers,
 * view-transition wrappers, TaskQueue scheduler, debounce/throttle, fetchConfig,
 * formatMoney, geometry helpers, and the shimmer-text loading element.
 * @customElement shimmer-text
 */
// Media queries
const mqReducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
export const mediaQueryLarge = matchMedia('(min-width: 750px)');

export const prefersReducedMotion = () => mqReducedMotion.matches;
export const isMobileBreakpoint = () => !mediaQueryLarge.matches;
export const isDesktopBreakpoint = () => mediaQueryLarge.matches;

export const supportsViewTransitions = () => typeof document.startViewTransition === 'function';

export function isMetaInAppBrowser(userAgent = navigator.userAgent) {
  return /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram)\b/i.test(userAgent || '');
}

export function shouldSkipViewTransitions() {
  return prefersReducedMotion() || isMetaInAppBrowser();
}

export const viewTransition = { current: undefined };

// Idle callback with setTimeout fallback
export const requestIdleCallback = window.requestIdleCallback?.bind(window) ?? setTimeout;

// Yield to main thread after next frame
export const requestYieldCallback = (cb) => {
  if (typeof cb !== 'function') return;
  requestAnimationFrame(() => setTimeout(cb, 0));
};

export const NEW_WINDOW_WARNING_ID = 'fit-a11y-new-window-message';
export const NEW_WINDOW_LINK_ATTRIBUTES =
  `target="_blank" rel="noopener noreferrer" aria-describedby="${NEW_WINDOW_WARNING_ID}"`;

// TaskQueue batches tasks into a single rAF, waiting for any active view transition
class TaskQueue {
  #queue = new Set();
  #pending = false;

  schedule = async (task) => {
    this.#queue.add(task);
    if (this.#pending) return;
    this.#pending = true;
    viewTransition.current && (await viewTransition.current);
    requestAnimationFrame(this.flush);
  };

  flush = () => {
    for (const t of this.#queue) t();
    this.#queue.clear();
    this.#pending = false;
  };
}

export const scheduler = new TaskQueue();

// Estimate how many product cards are visible for transition animation
const calcVisibleCards = (grid, cards) => {
  if (!grid || !cards?.length) return 0;
  const first = cards[0];
  if (!first) return 0;

  const gRect = grid.getBoundingClientRect();
  const vTop = Math.max(0, gRect.top);
  const vBottom = Math.min(window.innerHeight, gRect.bottom);
  const vHeight = Math.round(vBottom - vTop);
  if (vHeight <= 0) return 0;

  const cardEl = first.querySelector('product-tile, product-card');
  const cardGallery = cardEl?.querySelector?.('[data-ref="cardGallery"]');
  const cardContent = cardEl?.querySelector?.('.fc-product-item__content, .product-card__content, [ref="productCardLink"]');
  const gStyle = getComputedStyle(grid);

  const arStr =
    cardGallery?.style.getPropertyValue('--gallery-aspect-ratio') ??
    cardEl?.refs?.cardGallery?.style.getPropertyValue('--gallery-aspect-ratio') ??
    '';
  let ar = parseFloat(arStr) || 0.5;
  if (arStr.includes('/')) {
    const [w = '1', h = '2'] = arStr.split('/');
    ar = parseInt(w, 10) / parseInt(h, 10);
  }

  const cGap =
    parseInt(
      cardContent?.style.getPropertyValue('--product-item-gap') ||
        cardEl?.refs?.productCardLink?.style.getPropertyValue('--product-card-gap') ||
        '',
    ) || 12;
  const gGap = parseInt(gStyle.getPropertyValue('--product-grid-gap')) || 12;
  // Two lines of text estimate (title + price)
  const detailsH = ((parseInt(gStyle.fontSize) || 16) + 2) * 2;
  const mobile = window.innerWidth < 750;

  const cW = mobile ? Math.round((gRect.width - gGap) / 2) : 100;
  const cH = Math.round(cW / ar) + cGap + detailsH;

  const cols = mobile ? 2 : Math.floor((gRect.width + gGap) / (cW + gGap));
  const rows = Math.ceil((vHeight - gGap) / (cH + gGap));
  return cols * rows;
};

// View transition type handlers
const vtTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');
    const items = [...document.querySelectorAll('.product-grid .product-grid__item')];
    if (!grid || !items.length) return;

    await new Promise((done) =>
      requestIdleCallback(() => {
        const n = calcVisibleCards(grid, items);
        for (const [i, card] of items.entries()) {
          card.style.setProperty(
            i < n ? 'view-transition-name' : 'content-visibility',
            i < n ? `product-card-${card.dataset.productId}` : 'hidden',
          );
        }
        done(null);
      }),
    );

    // Cleanup: strip transition props from all cards
    return () => {
      for (const card of items) {
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      }
    };
  },
};

export function startViewTransition(cb, types) {
  if (typeof cb !== 'function') return Promise.resolve();

  return new Promise(async (resolve) => {
    if (supportsViewTransitions() && !shouldSkipViewTransitions()) {
      const cleanups = [];

      if (Array.isArray(types)) {
        for (const t of types) {
          const handler = typeof t === 'string' && vtTypes[t];
          if (!handler) continue;
          try {
            const fn = await handler();
            typeof fn === 'function' && cleanups.push(fn);
          } catch (_) {
            /* skip */
          }
        }
      }

      try {
        const tr = document.startViewTransition(cb);
        viewTransition.current ??= tr.finished;

        Array.isArray(types) &&
          types.forEach((t) => {
            typeof t === 'string' && tr.types?.add(t);
          });

        tr.finished
          .then(() => {
            viewTransition.current = undefined;
            cleanups.forEach((fn) => {
              try {
                fn();
              } catch (_) {
                /* skip */
              }
            });
            resolve();
          })
          .catch(() => resolve());
        return;
      } catch (_) {
        /* fall through to non-VT path */
      }
    }

    try {
      cb();
    } catch (_) {
      /* skip */
    }
    resolve();
  });
}

export function fetchConfig(type = 'json', cfg = {}) {
  const hdrs = {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    ...cfg.headers,
  };

  if (type === 'javascript') {
    hdrs['X-Requested-With'] = 'XMLHttpRequest';
    delete hdrs['Content-Type'];
  }

  return { method: 'POST', headers: hdrs, body: cfg.body };
}

export function debounce(fn, wait) {
  let tid;
  const wrapped = function (...args) {
    clearTimeout(tid);
    tid = setTimeout(() => fn.apply(this, args), wait);
  };
  wrapped.cancel = () => {
    clearTimeout(tid);
    tid = undefined;
  };
  return wrapped;
}

export function throttle(fn, delay) {
  let last = 0;
  const wrapped = function (...args) {
    const now = performance.now();
    if (now - last < delay) return;
    last = now;
    fn.apply(this, args);
  };
  wrapped.cancel = () => {
    last = performance.now();
  };
  return wrapped;
}

export const normalizeString = (s) =>
  typeof s === 'string'
    ? s
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
    : '';

export function formatMoney(val) {
  if (typeof val !== 'string') return '';
  const v = val.replace(' ', '');
  const ci = v.indexOf(',');
  const di = v.indexOf('.');
  if (ci === -1) return v;
  if (ci < di) return v.replace(',', '');
  if (di < ci) return v.replace('.', '').replace(',', '.');
  return v.replace(',', '.');
}

// Fire cb when document is fully loaded (all resources)
export const onDocumentLoaded = (cb) => {
  if (typeof cb !== 'function') return;
  document.readyState === 'complete' ? cb() : window.addEventListener('load', cb, { once: true });
};

// Fire cb when DOM is parsed (before sub-resources)
export const onDocumentReady = (cb) => {
  if (typeof cb !== 'function') return;
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', cb, { once: true }) : cb();
};

export function onAnimationEnd(els, cb, opts = { subtree: true }) {
  if (!els) return Promise.resolve();

  const list = (Array.isArray(els) ? els : [els]).filter((e) => e instanceof Element);
  if (!list.length) return Promise.resolve();

  const finished = list
    .flatMap((el) => el.getAnimations?.(opts) ?? [])
    .filter((a) => a?.timeline instanceof DocumentTimeline && a.finished)
    .map((a) => a.finished);

  if (!finished.length) {
    typeof cb === 'function' && cb();
    return Promise.resolve();
  }

  return Promise.allSettled(finished).then(() => {
    typeof cb === 'function' && cb();
  });
}

export const isPointWithinElement = (x, y, el) => {
  if (!el) return false;
  const { left, right, top, bottom } = el.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
};

export function isClickedOutside(evt, el) {
  if (!(evt instanceof MouseEvent) || !el) return false;

  // For dialog backdrops or non-Element targets, use coordinate check
  if (evt.target instanceof HTMLDialogElement || !(evt.target instanceof Element)) {
    return !isPointWithinElement(evt.clientX, evt.clientY, el);
  }
  return !el.contains(evt.target);
}

export const clamp = (v, lo, hi) => {
  if (!isFinite(v) || !isFinite(lo) || !isFinite(hi)) return v;
  if (lo > hi) [lo, hi] = [hi, lo];
  return Math.max(lo, Math.min(v, hi));
};

// Get center/start point of an element, optionally for a single axis
const pointFromRect = (el, axis, calcFn) => {
  if (!(el instanceof Element)) return axis ? 0 : { x: 0, y: 0 };
  const r = el.getBoundingClientRect();
  const pt = calcFn(r);
  return axis === 'x' || axis === 'y' ? pt[axis] : pt;
};

export const center = (el, axis) =>
  pointFromRect(el, axis, ({ left, width, top, height }) => ({
    x: left + width / 2,
    y: top + height / 2,
  }));

export const start = (el, axis) => pointFromRect(el, axis, ({ left, top }) => ({ x: left, y: top }));

export const closest = (vals, target) => {
  if (!Array.isArray(vals) || !vals.length) return target;
  if (!isFinite(target)) return vals[0];
  const valid = vals.filter(isFinite);
  if (!valid.length) return target;
  return valid.reduce((prev, cur) => (Math.abs(cur - target) < Math.abs(prev - target) ? cur : prev));
};

export const preventDefault = (e) => e?.preventDefault?.();

export function getVisibleElements(root, els, ratio = 1, axis) {
  if (!(root instanceof Element) || !Array.isArray(els) || !els.length) return [];
  if (!isFinite(ratio) || ratio < 0 || ratio > 1) ratio = 1;

  const rr = root.getBoundingClientRect();

  return els.filter((el) => {
    if (!(el instanceof Element)) return false;
    const { width: w, height: h, top, right, left, bottom } = el.getBoundingClientRect();

    if (ratio < 1) {
      const iw = Math.max(0, Math.min(rr.right, right) - Math.max(rr.left, left));
      if (axis === 'x') return w > 0 && iw / w >= ratio;
      const ih = Math.max(0, Math.min(rr.bottom, bottom) - Math.max(rr.top, top));
      if (axis === 'y') return h > 0 && ih / h >= ratio;
      return w * h > 0 && (iw * ih) / (w * h) >= ratio;
    }

    const inX = left >= rr.left && right <= rr.right;
    if (axis === 'x') return inX;
    const inY = top >= rr.top && bottom <= rr.bottom;
    return axis === 'y' ? inY : inX && inY;
  });
}

export function getIOSVersion() {
  const ua = navigator.userAgent;
  if (!/(iPhone|iPad)/i.test(ua)) return null;
  const ver = ua.match(/OS ([\d_]+)/)?.[1];
  if (!ver) return null;

  const [maj, min] = ver.split('_');
  const majN = parseInt(maj, 10);
  if (!isFinite(majN)) return null;

  const minN = parseInt(min, 10);
  return {
    fullString: ver.replace('_', '.'),
    major: majN,
    minor: isFinite(minN) ? minN : 0,
  };
}

export const preloadImage = (src) => {
  if (typeof src !== 'string' || !src) return;
  const img = new Image();
  img.src = src;
};

export class ShimmerText extends HTMLElement {
  shimmer() {
    this.setAttribute('shimmer', '');
  }
}

if (!customElements.get('shimmer-text')) {
  customElements.define('shimmer-text', ShimmerText);
}

export const resetShimmer = (ctr = document.body) => {
  if (!(ctr instanceof Element)) return;
  for (const el of ctr.querySelectorAll('[shimmer]')) el.removeAttribute('shimmer');
};

export function changeMetaThemeColor(srcEl) {
  if (!(srcEl instanceof Element)) return;
  const meta = document.head?.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const bg = getComputedStyle(srcEl).backgroundColor;
  bg && meta.setAttribute('content', bg);
}

FitCheck.utilities = { ...FitCheck.utilities, scheduler };
